export type WorkflowCommunicationActionType =
  | 'SEND_MARKETING_EMAIL'
  | 'SEND_ONE_TO_ONE_EMAIL'
  | 'SEND_INTERNAL_NOTIFICATION'
  | 'SEND_TEAMS_NOTIFICATION'
  | 'SEND_SLACK_NOTIFICATION'
  | 'SEND_SMS'
  | 'SEND_WHATSAPP';

export interface WorkflowActionExecuteRequest {
  context?: Record<string, any>;
}

export interface WorkflowActionExecuteResponse {
  enrollmentId?: number;
  executionRunId?: number;
  actionNodeId?: number;
  actionType?: WorkflowCommunicationActionType | string;
  status?: 'SUCCESS' | 'FAILED' | 'SKIPPED' | string;
  targetObjectType?: string | null;
  targetObjectId?: number | null;
  message?: string;
  nextNodeId?: number | null;
  nextNodeType?: string | null;
  output?: Record<string, any>;
  error?: Record<string, any>;
}

export interface WorkflowEmailPreviewRequest {
  workflowId: number;
  enrollmentId: number;
  nodeId: number;
  context?: Record<string, any>;
}

/** Tipo de destinatario resuelto por el backend (orden de salida: TO → CC → BCC). */
export type WorkflowEmailRecipientKind = 'TO' | 'CC' | 'BCC';
/** De dónde salió el destinatario. */
export type WorkflowEmailRecipientSource = 'RECORD_CONTACT' | 'CRM_USER' | 'CUSTOM_EMAIL';

/** Destinatario resuelto que devuelven la vista previa, el probador y la salida del paso. */
export interface WorkflowEmailRecipientInfo {
  kind?: WorkflowEmailRecipientKind | string;
  email?: string;
  name?: string | null;
  source?: WorkflowEmailRecipientSource | string;
  contactId?: number | null;
  userId?: number | null;
}

export const WORKFLOW_EMAIL_RECIPIENT_KIND_LABELS: Record<string, string> = {
  TO: 'Para',
  CC: 'CC',
  BCC: 'CCO'
};

export const WORKFLOW_EMAIL_RECIPIENT_SOURCE_LABELS: Record<string, string> = {
  RECORD_CONTACT: 'Contacto del registro',
  CRM_USER: 'Usuario del CRM',
  CUSTOM_EMAIL: 'Correo externo'
};

export interface WorkflowEmailPreviewResponse {
  subject?: string;
  body?: string;
  /** COMPAT: primer destinatario de "Para". La lista completa viaja en `recipients`. */
  recipient?: string;
  recipients?: WorkflowEmailRecipientInfo[];
  warnings?: string[];
}

export interface WorkflowTokenRenderRequest {
  enrollmentId: number;
  text: string;
  context?: Record<string, any>;
  keepUnresolved?: boolean;
}

export interface WorkflowTokenRenderResponse {
  renderedText: string;
  unresolvedTokens?: string[];
  warnings?: string[];
}

export interface WorkflowActionTestRequest {
  actionType: WorkflowCommunicationActionType | string;
  objectType?: string;
  config: Record<string, any>;
  context?: Record<string, any>;
}

export interface WorkflowActionTestResponse {
  actionType?: string;
  status?: string;
  message?: string;
  output?: Record<string, any>;
  warnings?: string[];
}

// Quick examples for Action Tester
export const ACTION_TEST_EXAMPLES: Record<string, { config: Record<string, any>; context: Record<string, any> }> = {
  SEND_INTERNAL_NOTIFICATION: {
    config: {
      recipientType: 'OWNER',
      subject: 'Nuevo lead',
      message: 'El contacto {{contact.firstname}} calificó.',
      channel: 'IN_APP',
      continueOnError: true
    },
    context: { contact: { firstname: 'Juan', email: 'juan@test.com' } }
  },
  SEND_MARKETING_EMAIL: {
    config: {
      emailTemplateId: 1,
      subject: 'Hola {{contact.firstname}}',
      recipients: { includeRecordContact: true, emailProperty: 'email' },
      cc: { userIds: [], emails: [] },
      bcc: { userIds: [], emails: [] },
      respectSubscription: true,
      continueOnError: false
    },
    context: { contact: { firstname: 'Juan', email: 'juan@test.com' }, marketingSubscribed: true }
  },
  SEND_ONE_TO_ONE_EMAIL: {
    config: {
      subject: 'Seguimiento de tu cuenta',
      body: 'Hola {{contact.firstname}}, quería darte seguimiento...',
      fromOwner: true,
      recipients: { includeRecordContact: true, emailProperty: 'email', userIds: [], emails: [] },
      cc: { userIds: [], emails: [] },
      bcc: { userIds: [], emails: [] },
      continueOnError: false
    },
    context: { contact: { firstname: 'Juan', email: 'juan@test.com' } }
  },
  SEND_TEAMS_NOTIFICATION: {
    config: {
      teamChannelId: 'general',
      message: 'Nuevo lead: {{contact.firstname}}',
      continueOnError: true
    },
    context: { contact: { firstname: 'Juan' } }
  }
};
